import { Fragment, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SnoozeIcon from '@mui/icons-material/Snooze';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { retrieveTask } from '@src/api/tasks';
import dayjs from 'dayjs';
import { Link as RouterLink } from 'react-router-dom';
import TaskExpandedContent, { SOURCE_BADGE, deepLinkForTask } from './TaskExpandedContent';

const PRIORITY_LABEL = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

const SORT_FIELD_OPTIONS = [
  { value: 'created_at', label: 'Created date' },
  { value: 'due_date', label: 'Due date' },
];

const SORT_DIRECTION_OPTIONS = [
  { value: 'asc', label: 'Oldest first' },
  { value: 'desc', label: 'Newest first' },
];

function taskStateColor(task) {
  if (task.is_completed) return { color: 'success.main', label: 'Done' };
  if (task.snoozed_until && dayjs(task.snoozed_until).isAfter(dayjs())) {
    return { color: 'warning.main', label: `Snoozed until ${dayjs(task.snoozed_until).format('MMM D')}` };
  }
  return { color: 'grey.500', label: 'Open' };
}

export function SnoozeMenu({ task, onSnooze }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClose = () => setAnchorEl(null);

  const isSnoozed = Boolean(
    task.snoozed_until && dayjs(task.snoozed_until).isAfter(dayjs()),
  );

  const handleSnooze = (delta) => {
    handleClose();
    if (delta === null) {
      onSnooze(task, null);
      return;
    }
    const until = dayjs().add(delta.amount, delta.unit).hour(9).minute(0).second(0).toISOString();
    onSnooze(task, until);
  };

  const handleCustom = () => {
    handleClose();
    const input = window.prompt(
      'Snooze until when? (YYYY-MM-DD or YYYY-MM-DD HH:mm)',
      dayjs().add(1, 'week').format('YYYY-MM-DD'),
    );
    if (!input) return;
    const parsed = dayjs(input);
    if (!parsed.isValid()) {
      window.alert('Invalid date. Use YYYY-MM-DD or YYYY-MM-DD HH:mm.');
      return;
    }
    onSnooze(task, parsed.toISOString());
  };

  return (
    <>
      <Tooltip title={isSnoozed ? 'Snoozed — change or wake up' : 'Snooze'}>
        <IconButton
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          sx={{ color: isSnoozed ? 'warning.main' : 'text.secondary' }}
        >
          <SnoozeIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={() => handleSnooze({ amount: 1, unit: 'day' })}>Tomorrow 9am</MenuItem>
        <MenuItem onClick={() => handleSnooze({ amount: 3, unit: 'day' })}>In 3 days</MenuItem>
        <MenuItem onClick={() => {
          handleClose();
          // Next Monday 9am
          const today = dayjs();
          const daysUntilMonday = (8 - today.day()) % 7 || 7;
          const until = today.add(daysUntilMonday, 'day').hour(9).minute(0).second(0).toISOString();
          onSnooze(task, until);
        }}>Next Monday</MenuItem>
        <MenuItem onClick={() => handleSnooze({ amount: 2, unit: 'week' })}>In 2 weeks</MenuItem>
        <MenuItem onClick={handleCustom}>Custom date…</MenuItem>
        {isSnoozed && [
          <Divider key="divider" />,
          <MenuItem key="wake" onClick={() => handleSnooze(null)}>Wake up now</MenuItem>,
        ]}
      </Menu>
    </>
  );
}

SnoozeMenu.propTypes = {
  task: PropTypes.shape({
    id: PropTypes.number,
    snoozed_until: PropTypes.string,
  }).isRequired,
  onSnooze: PropTypes.func.isRequired,
};

/**
 * Overflow menu for the row's secondary actions (Edit, Delete).
 * Shown only on mobile where horizontal space is tight; on desktop the
 * Edit/Delete icons appear inline instead.
 */
function RowOverflowMenu({ task, onEdit, onDelete }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClose = () => setAnchorEl(null);
  return (
    <>
      <Tooltip title="More">
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={() => { handleClose(); onEdit(task); }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => { handleClose(); onDelete(task); }}
          sx={{ color: 'error.main' }}
        >
          <DeleteOutlineIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </>
  );
}

RowOverflowMenu.propTypes = {
  task: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

// DetailBlock, deepLinkForTask, DomainPreviewCard, SourceEmailPanel, and
// ProposalCard moved to TaskExpandedContent.jsx (shared with TaskFocusMode).

// Per-task ActivityHistoryDialog was removed — TaskActivity model + endpoint
// stay in place for the future dedicated audit view + calibration data.


function TaskTable({
  tasks,
  loading,
  sortConfig,
  selectedIds,
  onEdit,
  onToggleComplete,
  onDelete,
  onSortChange,
  onSelectionChange,
  onBulkDelete,
  onBulkComplete,
  onSnooze,
  onApproveProposal,
  onRejectProposal,
  onDomainApproved,
  onRefreshTask,
}) {
  const [expandedRows, setExpandedRows] = useState({});
  const lastClickedIndexRef = useRef(null);

  // After a reply is sent from the inline email panel the backend closes the
  // task — re-fetch it for fresh state and collapse the row so the triage
  // flow moves on to the next task instead of lingering on a done one.
  const handleEmailTaskUpdated = (task) => {
    setExpandedRows((prev) => ({ ...prev, [task.id]: false }));
    if (!onRefreshTask) return;
    retrieveTask(task.id)
      .then((response) => onRefreshTask(response.data))
      .catch(() => onRefreshTask({ ...task, is_completed: true }));
  };

  const taskIds = useMemo(() => tasks.map((t) => t.id), [tasks]);
  const allSelected = taskIds.length > 0 && taskIds.every((id) => selectedIds.has(id));
  const someSelected = !allSelected && taskIds.some((id) => selectedIds.has(id));

  const toggleExpanded = (taskId) => {
    setExpandedRows((prev) => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const handleRowSelect = (taskId, index, event) => {
    const next = new Set(selectedIds);
    const isShift = event.nativeEvent && event.nativeEvent.shiftKey;
    const lastIndex = lastClickedIndexRef.current;

    if (isShift && lastIndex !== null && lastIndex !== undefined) {
      const start = Math.min(lastIndex, index);
      const end = Math.max(lastIndex, index);
      const shouldSelect = !next.has(taskId);
      for (let i = start; i <= end; i += 1) {
        const id = taskIds[i];
        if (id == null) continue;
        if (shouldSelect) {
          next.add(id);
        } else {
          next.delete(id);
        }
      }
    } else if (next.has(taskId)) {
      next.delete(taskId);
    } else {
      next.add(taskId);
    }
    lastClickedIndexRef.current = index;
    onSelectionChange(next);
  };

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(taskIds));
    }
    lastClickedIndexRef.current = null;
  };

  const expandedCount = useMemo(
    () => Object.values(expandedRows).filter(Boolean).length,
    [expandedRows],
  );

  const selectedCount = selectedIds.size;

  return (
    <Card variant="outlined">
      <CardHeader
        title="Table view"
        subheader={
          selectedCount
            ? `${selectedCount} selected. Shift-click to extend the range.`
            : `Built for triage and detail review. ${expandedCount ? `${expandedCount} row${expandedCount === 1 ? '' : 's'} expanded.` : ''}`
        }
        action={
          selectedCount ? (
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => onSelectionChange(new Set())}
              >
                Clear
              </Button>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={onBulkComplete}
              >
                Complete {selectedCount}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={onBulkDelete}
              >
                Delete {selectedCount}
              </Button>
            </Stack>
          ) : (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ minWidth: { md: 380 } }}>
              <TextField
                select
                size="small"
                label="Sort by"
                value={sortConfig.field}
                onChange={(event) => onSortChange(event.target.value, sortConfig.direction)}
              >
                {SORT_FIELD_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                size="small"
                label="Order"
                value={sortConfig.direction}
                onChange={(event) => onSortChange(sortConfig.field, event.target.value)}
              >
                {SORT_DIRECTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )
        }
      />
      <CardContent>
        {loading ? (
          <Box sx={{ py: 6, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={32} />
          </Box>
        ) : tasks.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No tasks found. Try adjusting your filters or create a new task.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell padding="checkbox" />
                  <TableCell>Task</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Source</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Client</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Assignee</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Due date</TableCell>
                  <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Created</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Priority</TableCell>
                  <TableCell padding="checkbox">State</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tasks.map((task, index) => {
                  const isExpanded = Boolean(expandedRows[task.id]);
                  const isSelected = selectedIds.has(task.id);
                  const subtaskCount = task.subtasks_count ?? (task.subtasks || []).length;
                  const hasProposal = Boolean(
                    (task.ai_proposed_actions && task.ai_proposed_actions.length) ||
                    (task.ai_proposed_reply || '').trim(),
                  );
                  const hasDetails = Boolean(
                    hasProposal ||
                    task.domain_preview ||
                    task.source_key ||
                    task.notes ||
                    task.description ||
                    subtaskCount,
                  );
                  const sourceBadge = task.source_domain ? SOURCE_BADGE[task.source_domain] : null;
                  const deepLink = deepLinkForTask(task);

                  return (
                    <Fragment key={task.id}>
                      <TableRow hover selected={isSelected}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected}
                            onClick={(event) => handleRowSelect(task.id, index, event)}
                            onChange={() => {}}
                          />
                        </TableCell>
                        <TableCell padding="checkbox">
                          <IconButton
                            size="small"
                            onClick={() => toggleExpanded(task.id)}
                            disabled={!hasDetails}
                          >
                            {isExpanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ minWidth: { xs: 0, md: 280 }, maxWidth: { xs: '100%', md: 420 } }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {task.title}
                          </Typography>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                            {/* Source badge moves into the title cell on mobile since the Source column is hidden. */}
                            <Box sx={{ display: { xs: 'inline-flex', md: 'none' } }}>
                              {sourceBadge ? (
                                <Chip size="small" label={sourceBadge.label} color={sourceBadge.color} variant="outlined" />
                              ) : (
                                <Chip size="small" label="Manual" variant="outlined" />
                              )}
                            </Box>
                            {hasProposal ? (
                              <Chip
                                size="small"
                                label={task.ai_confidence != null ? `🤖 ${task.ai_confidence}%` : '🤖 AI proposal'}
                                color="primary"
                              />
                            ) : null}
                            {subtaskCount ? (
                              <Chip size="small" label={`${subtaskCount} subtask${subtaskCount === 1 ? '' : 's'}`} variant="outlined" />
                            ) : null}
                          </Stack>
                          {/* Mobile-only meta line: client · due · priority — since those columns are hidden. */}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: { xs: 'block', md: 'none' }, mt: 0.5 }}
                          >
                            {[
                              task.client_name || 'Internal',
                              task.due_date ? `Due ${dayjs(task.due_date).format('MMM D')}` : null,
                              PRIORITY_LABEL[task.priority] || null,
                            ].filter(Boolean).join(' · ')}
                          </Typography>
                          {task.notes ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }} noWrap>
                              {task.notes}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell sx={{ minWidth: 110, display: { xs: 'none', md: 'table-cell' } }}>
                          {sourceBadge ? (
                            <Chip
                              size="small"
                              label={sourceBadge.label}
                              color={sourceBadge.color}
                              variant="outlined"
                            />
                          ) : (
                            <Chip size="small" label="Manual" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          {task.client_name || <Chip size="small" label="Internal" variant="outlined" />}
                        </TableCell>
                        <TableCell sx={{ minWidth: 160, display: { xs: 'none', lg: 'table-cell' } }}>
                          {task.assignee_name ? (
                            <Chip size="small" color="primary" variant="outlined" label={task.assignee_name} />
                          ) : (
                            <Chip size="small" label="Unassigned" />
                          )}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          {task.due_date ? dayjs(task.due_date).format('MMM D, YYYY') : '—'}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                          {task.created_at ? (
                            <Tooltip title={dayjs(task.created_at).format('MMM D, YYYY h:mm A')}>
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                {dayjs(task.created_at).fromNow()}
                              </Typography>
                            </Tooltip>
                          ) : '—'}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{PRIORITY_LABEL[task.priority] || task.priority_display || '—'}</TableCell>
                        <TableCell padding="checkbox">
                          {(() => {
                            const state = taskStateColor(task);
                            return (
                              <Tooltip title={state.label}>
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: state.color,
                                    mx: 'auto',
                                  }}
                                />
                              </Tooltip>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={task.is_completed ? 'Reopen' : 'Mark done'}>
                            <IconButton
                              size="small"
                              onClick={() => onToggleComplete(task, !task.is_completed)}
                              sx={{ color: task.is_completed ? 'success.main' : 'text.secondary' }}
                            >
                              {task.is_completed ? (
                                <CheckCircleIcon fontSize="small" />
                              ) : (
                                <CheckCircleOutlineIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <SnoozeMenu task={task} onSnooze={onSnooze} />
                          {deepLink ? (
                            <Tooltip title={deepLink.label}>
                              <IconButton
                                size="small"
                                component={RouterLink}
                                to={deepLink.to}
                                sx={{ color: 'primary.main' }}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                          {/* Mobile: collapse Edit + Delete behind a kebab menu so
                              Done + Snooze + Open get the row's limited width. */}
                          <Box sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
                            <RowOverflowMenu task={task} onEdit={onEdit} onDelete={onDelete} />
                          </Box>
                          <Tooltip title="Edit task">
                            <IconButton
                              size="small"
                              onClick={() => onEdit(task)}
                              sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete task">
                            <IconButton
                              size="small"
                              onClick={() => onDelete(task)}
                              sx={{ color: 'error.main', display: { xs: 'none', sm: 'inline-flex' } }}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={11} sx={{ py: 0, borderBottom: isExpanded ? undefined : 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <TaskExpandedContent
                              task={task}
                              onApproveProposal={onApproveProposal}
                              onRejectProposal={onRejectProposal}
                              onRefreshTask={onRefreshTask}
                              onDomainApproved={onDomainApproved}
                              onEmailTaskUpdated={handleEmailTaskUpdated}
                            />
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
}

TaskTable.propTypes = {
  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      source_key: PropTypes.string,
      source_domain: PropTypes.string,
      subtasks_count: PropTypes.number,
      snoozed_until: PropTypes.string,
    }),
  ),
  loading: PropTypes.bool,
  sortConfig: PropTypes.shape({
    field: PropTypes.oneOf(['created_at', 'due_date']).isRequired,
    direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
  }).isRequired,
  selectedIds: PropTypes.instanceOf(Set).isRequired,
  onEdit: PropTypes.func.isRequired,
  onToggleComplete: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onSelectionChange: PropTypes.func.isRequired,
  onBulkDelete: PropTypes.func.isRequired,
  onBulkComplete: PropTypes.func.isRequired,
  onSnooze: PropTypes.func.isRequired,
  onApproveProposal: PropTypes.func.isRequired,
  onRejectProposal: PropTypes.func.isRequired,
  onDomainApproved: PropTypes.func,
  onRefreshTask: PropTypes.func,
};

TaskTable.defaultProps = {
  tasks: [],
  loading: false,
  onDomainApproved: null,
  onRefreshTask: null,
};

export default TaskTable;
